import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type CredenzialiProvvisorie,
  type NuovaPersona,
  type RepartoDisponibile,
  type VoceAnagrafica,
  ErroreRisposta,
  api,
} from '../api/client.ts';
import type { Ruolo } from '../api/tipi.ts';
import stili from './Anagrafica.module.css';

/**
 * Anagrafica: le persone, i loro accessi e i loro perimetri.
 *
 * Riservata agli amministratori. Elenco a sinistra, scheda a destra: con 184
 * persone una pagina per volta costringerebbe a tornare indietro a ogni
 * modifica, e la ricerca serve piu' della navigazione.
 *
 * Non esiste un pulsante per cancellare. Si chiude il rapporto: la persona
 * resta con la sua storia e sparisce dal calendario dei giorni successivi.
 * Cancellarla porterebbe via anche le sue assenze passate.
 */

const RUOLI: { codice: Ruolo; etichetta: string; spiegazione: string }[] = [
  { codice: 'UTENTE_BASE', etichetta: 'Utente base', spiegazione: 'Inserisce le proprie assenze.' },
  {
    codice: 'FUNZIONARIO',
    etichetta: 'Funzionario',
    spiegazione: 'Come utente base, piu’ il programma settimanale.',
  },
  {
    codice: 'RESPONSABILE',
    etichetta: 'Responsabile',
    spiegazione: 'Approva e rifiuta le richieste altrui. Niente anagrafica.',
  },
  {
    codice: 'AMMINISTRATORE',
    etichetta: 'Amministratore',
    spiegazione: 'Tutto, anagrafica compresa.',
  },
];

/**
 * La sigla mostrata accanto al nome nell'elenco.
 *
 * E' un Record tipato su tutti i ruoli, non una catena di condizioni: prima
 * questa etichetta era "amministratore, altrimenti funzionario", e quando e'
 * arrivato il Responsabile e' finito nel ramo sbagliato senza che nulla se ne
 * accorgesse. Cosi' invece aggiungere un ruolo non compila finche' non gli si
 * e' dato un posto qui.
 *
 * `null` significa nessuna sigla: l'utente base e' la normalita' e non ha
 * bisogno di essere marcato in un elenco di 183 righe.
 */
const SIGLA_RUOLO: Record<Ruolo, string | null> = {
  UTENTE_BASE: null,
  FUNZIONARIO: 'funz.',
  RESPONSABILE: 'resp.',
  AMMINISTRATORE: 'admin',
};

export interface ProprietaAnagrafica {
  /** Serve a impedire all'interfaccia di offrire azioni su se stessi. */
  dipendenteProprio: string;
}

export function Anagrafica({ dipendenteProprio }: ProprietaAnagrafica) {
  const [persone, setPersone] = useState<VoceAnagrafica[]>([]);
  const [reparti, setReparti] = useState<RepartoDisponibile[]>([]);
  const [selezionato, setSelezionato] = useState<string | null>(null);
  const [ricerca, setRicerca] = useState('');
  const [mostraCessati, setMostraCessati] = useState(false);
  const [inCreazione, setInCreazione] = useState(false);
  const [credenziali, setCredenziali] = useState<CredenzialiProvvisorie | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [caricamento, setCaricamento] = useState(true);

  const carica = useCallback(async () => {
    try {
      const [elenco, disponibili] = await Promise.all([
        api.anagrafica(),
        api.repartiDisponibili(),
      ]);
      setPersone(elenco);
      setReparti(disponibili);
      setErrore(null);
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Caricamento non riuscito.');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  const visibili = useMemo(() => {
    const cercato = ricerca.trim().toLowerCase();
    return persone.filter((p) => {
      if (!mostraCessati && p.stato === 'CESSATO') return false;
      if (cercato === '') return true;
      return (
        p.nomeCompleto.toLowerCase().includes(cercato) ||
        (p.username ?? '').includes(cercato) ||
        p.repartoNome.toLowerCase().includes(cercato) ||
        p.sedeNome.toLowerCase().includes(cercato)
      );
    });
  }, [persone, ricerca, mostraCessati]);

  const persona = useMemo(
    () => persone.find((p) => p.id === selezionato) ?? null,
    [persone, selezionato],
  );

  const cessati = persone.filter((p) => p.stato === 'CESSATO').length;

  if (caricamento) return <p className={stili.attesa}>Caricamento dell’anagrafica…</p>;

  return (
    <div className={stili.pagina}>
      <div className={stili.colonnaElenco}>
        <div className={stili.barra}>
          <input
            type="search"
            className={stili.ricerca}
            placeholder="Cerca per nome, username, reparto…"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
          />
          <button
            type="button"
            className={stili.pulsanteNuovo}
            onClick={() => {
              setInCreazione(true);
              setSelezionato(null);
              setCredenziali(null);
            }}
          >
            + Nuova persona
          </button>
        </div>

        <label className={stili.interruttoreCessati}>
          <input
            type="checkbox"
            checked={mostraCessati}
            onChange={(e) => setMostraCessati(e.target.checked)}
          />
          Mostra anche i cessati ({cessati})
        </label>

        <p className={stili.conteggio}>
          {visibili.length} {visibili.length === 1 ? 'persona' : 'persone'}
        </p>

        <ul className={stili.elenco}>
          {visibili.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={`${stili.voce} ${selezionato === p.id ? stili.voceAttiva : ''} ${
                  p.stato === 'CESSATO' ? stili.voceCessata : ''
                }`}
                onClick={() => {
                  setSelezionato(p.id);
                  setInCreazione(false);
                  setCredenziali(null);
                }}
              >
                <span className={stili.voceNome}>{p.nomeCompleto}</span>
                <span className={stili.voceDettaglio}>
                  {p.tecnico ? 'Account di servizio' : `${p.sedeNome} · ${p.repartoNome}`}
                  {SIGLA_RUOLO[p.ruolo] !== null && (
                    <span
                      className={`${stili.pastigliaRuolo} ${
                        stili[`pastiglia${p.ruolo}`] ?? ''
                      }`}
                    >
                      {SIGLA_RUOLO[p.ruolo]}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className={stili.colonnaScheda}>
        {errore && <p className={stili.errore}>{errore}</p>}

        {credenziali && (
          <RiquadroCredenziali credenziali={credenziali} onChiudi={() => setCredenziali(null)} />
        )}

        {inCreazione && (
          <ModuloNuova
            reparti={reparti}
            onAnnulla={() => setInCreazione(false)}
            onCreata={async (cred) => {
              setCredenziali(cred);
              setInCreazione(false);
              await carica();
            }}
          />
        )}

        {!inCreazione && persona && (
          <Scheda
            /*
             * La chiave e' l'id della persona, e non e' un dettaglio: senza,
             * React riusa lo stesso componente passando da una scheda
             * all'altra, i campi non controllati mantengono il testo di prima
             * e al primo clic fuori lo salvano sulla persona sbagliata. E'
             * successo davvero con l'email.
             */
            key={persona.id}
            persona={persona}
            reparti={reparti}
            eSeStesso={persona.id === dipendenteProprio}
            onAggiornata={carica}
            onCredenziali={setCredenziali}
          />
        )}

        {!inCreazione && !persona && !credenziali && (
          <p className={stili.attesa}>Scegli una persona dall’elenco, o creane una nuova.</p>
        )}
      </div>
    </div>
  );
}

/**
 * La password provvisoria si vede UNA VOLTA SOLA.
 *
 * Il riquadro non si chiude da solo e non si chiude cliccando fuori: serve
 * un gesto esplicito. Chi la perde non puo' recuperarla, puo' solo azzerarla
 * di nuovo — ed e' giusto cosi', ma va detto prima, non scoperto dopo.
 */
function RiquadroCredenziali({
  credenziali,
  onChiudi,
}: {
  credenziali: CredenzialiProvvisorie;
  onChiudi: () => void;
}) {
  return (
    <div className={stili.credenziali}>
      <h3 className={stili.credenzialiTitolo}>Password provvisoria</h3>
      <p className={stili.credenzialiAvviso}>
        Annota <strong>tutte e due</strong>: la password non è salvata in chiaro da
        nessuna parte e non potrà essere rivista. Se la perdi, l’unico rimedio è
        azzerarla di nuovo.
      </p>
      <dl className={stili.credenzialiDati}>
        <dt>Nome utente</dt>
        {/* Grande quanto la password: non e' il nome scritto nel modulo, e chi
            lo da' per scontato non riesce ad entrare. */}
        <dd><code className={stili.password}>{credenziali.username}</code></dd>
        <dt>Password</dt>
        <dd><code className={stili.password}>{credenziali.passwordProvvisoria}</code></dd>
      </dl>
      <p className={stili.credenzialiNota}>
        Il nome utente è generato nella forma <code>nome.cognome</code>: non coincide
        con il nome scritto nel modulo. Al primo accesso verrà chiesto di cambiare la
        password.
      </p>
      <button type="button" className={stili.pulsantePrimario} onClick={onChiudi}>
        Ho annotato la password
      </button>
    </div>
  );
}

function ModuloNuova({
  reparti,
  onAnnulla,
  onCreata,
}: {
  reparti: RepartoDisponibile[];
  onAnnulla: () => void;
  onCreata: (credenziali: CredenzialiProvvisorie) => void | Promise<void>;
}) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [dati, setDati] = useState<NuovaPersona>({
    nomeCompleto: '',
    repartoId: reparti[0]?.id ?? '',
    ruolo: 'UTENTE_BASE',
    dataAssunzione: oggi,
    email: '',
    matricola: '',
    codiceFiscale: '',
    orarioSettimanale: [...ORARIO_UFFICI],
    tecnico: false,
  });
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const completo = dati.nomeCompleto.trim().length >= 3 && dati.repartoId !== '';

  return (
    <form
      className={stili.scheda}
      onSubmit={async (e) => {
        e.preventDefault();
        setErrore(null);
        setInCorso(true);
        try {
          const creata = await api.creaPersona({
            ...dati,
            nomeCompleto: dati.nomeCompleto.trim(),
            // Stringhe vuote diventano assenza di dato: un'email vuota nel
            // database sarebbe indistinguibile da una sbagliata.
            email: dati.email?.trim() || null,
            matricola: dati.matricola?.trim() || null,
            codiceFiscale: dati.codiceFiscale?.trim().toUpperCase() || null,
          });
          await onCreata(creata);
        } catch (err) {
          setErrore(err instanceof ErroreRisposta ? err.message : 'Creazione non riuscita.');
        } finally {
          setInCorso(false);
        }
      }}
    >
      <h2 className={stili.titolo}>Nuova persona</h2>
      <p className={stili.sottotitolo}>
        Viene creato anche l’accesso: nome utente e password provvisoria compaiono
        subito dopo.
      </p>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Nome completo</span>
        <input
          className={stili.input}
          value={dati.nomeCompleto}
          placeholder="Cognome Nome"
          onChange={(e) => setDati({ ...dati, nomeCompleto: e.target.value })}
          autoFocus
        />
        <span className={stili.aiuto}>
          Nell’ordine usato dal foglio: prima il cognome, poi il nome.
        </span>
        {/* Senza questo avviso ci si aspetta di poter entrare con il nome
            scritto qui sopra, e non funziona: e' successo davvero. */}
        <span className={stili.aiuto}>
          <strong>Il nome utente sarà diverso:</strong> viene generato come{' '}
          <code>nome.cognome</code> — scrivendo “Rossi Mario” diventa{' '}
          <code>mario.rossi</code>. Lo trovi subito dopo, insieme alla password.
        </span>
      </label>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Sede e reparto</span>
        <select
          className={stili.input}
          value={dati.repartoId}
          onChange={(e) => setDati({ ...dati, repartoId: e.target.value })}
        >
          {reparti.map((r) => (
            <option key={r.id} value={r.id}>
              {r.sedeNome} · {r.nome}
            </option>
          ))}
        </select>
      </label>

      <fieldset className={stili.campo}>
        <legend className={stili.etichetta}>Ruolo</legend>
        {RUOLI.map((r) => (
          <label key={r.codice} className={stili.sceltaRuolo}>
            <input
              type="radio"
              name="ruolo"
              checked={dati.ruolo === r.codice}
              onChange={() => setDati({ ...dati, ruolo: r.codice })}
            />
            <span>
              <strong>{r.etichetta}</strong>
              <span className={stili.aiuto}> {r.spiegazione}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Data di assunzione</span>
        <input
          type="date"
          className={stili.input}
          value={dati.dataAssunzione}
          onChange={(e) => setDati({ ...dati, dataAssunzione: e.target.value })}
        />
        <span className={stili.aiuto}>
          Prima di questa data non comparirà nel calendario.
        </span>
      </label>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Email (facoltativa)</span>
        <input
          type="email"
          className={stili.input}
          value={dati.email ?? ''}
          placeholder="lasciare vuoto se non ne ha una"
          onChange={(e) => setDati({ ...dati, email: e.target.value })}
        />
        <span className={stili.aiuto}>
          Senza email non riceverà l’avviso di approvazione. Tutto il resto funziona.
        </span>
      </label>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Codice fiscale (facoltativo)</span>
        <input
          className={stili.input}
          value={dati.codiceFiscale ?? ''}
          maxLength={16}
          placeholder="16 caratteri"
          // Maiuscolo mentre si scrive: e' lo stesso codice comunque lo si
          // digiti, e l'unicita' in banca dati distingue le maiuscole.
          onChange={(e) =>
            setDati({ ...dati, codiceFiscale: e.target.value.toUpperCase() })
          }
        />
      </label>

      <label className={stili.campo}>
        <span className={stili.etichetta}>Matricola (facoltativa)</span>
        <input
          className={stili.input}
          value={dati.matricola ?? ''}
          onChange={(e) => setDati({ ...dati, matricola: e.target.value })}
        />
      </label>

      <fieldset className={stili.campo}>
        <legend className={stili.etichetta}>Orario</legend>
        <SceltaOrario
          orario={dati.orarioSettimanale}
          onOrario={(orario) => setDati({ ...dati, orarioSettimanale: orario })}
        />
      </fieldset>

      <fieldset className={stili.campo}>
        <legend className={stili.etichetta}>Genere di utenza</legend>
        <label className={stili.sceltaRuolo}>
          <input
            type="checkbox"
            checked={dati.tecnico === true}
            onChange={(e) => setDati({ ...dati, tecnico: e.target.checked })}
          />
          <span>
            <strong>Account di servizio</strong>
            <span className={stili.aiuto}>
              {' '}
              Non è una persona: resta fuori dal calendario, dall’analisi e da “chi è
              assente”. Si usa per un accesso condiviso di amministrazione.
            </span>
          </span>
        </label>
        {dati.tecnico === true && (
          <span className={stili.aiuto}>
            Si dichiara adesso e non si cambia dopo: trasformare più tardi un
            dipendente in account di servizio gli farebbe sparire le assenze dalla
            calendario senza cancellarle.
          </span>
        )}
      </fieldset>

      {errore && <p className={stili.errore}>{errore}</p>}

      <div className={stili.azioni}>
        <button type="button" className={stili.pulsanteSecondario} onClick={onAnnulla}>
          Annulla
        </button>
        <button type="submit" className={stili.pulsantePrimario} disabled={!completo || inCorso}>
          {inCorso ? 'Creazione…' : 'Crea persona e accesso'}
        </button>
      </div>
    </form>
  );
}

/** Le ore proposte spuntando "part-time": il caso piu' comune. */
const GIORNI_SETTIMANA = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Negli uffici si lavora dal lunedi' al venerdi'; in produzione i turni
 *  cadono anche nel fine settimana (decisione 54). */
const ORARIO_UFFICI = [8, 8, 8, 8, 8, 0, 0];
const ORARIO_PRODUZIONE = [8, 8, 8, 8, 8, 8, 8];

const MODELLI = [
  { etichetta: 'Uffici', orario: ORARIO_UFFICI },
  { etichetta: 'Produzione', orario: ORARIO_PRODUZIONE },
];

/** 7 invece di 7,00; 4,5 invece di 4.5. */
function scrivi(ore: number): string {
  return String(Math.round(ore * 100) / 100).replace('.', ',');
}

/**
 * Tempo pieno o part-time, e in quel caso quanto vale una giornata.
 *
 * Una casella e un numero, non due dati indipendenti: il part-time E'
 * l'orario ridotto. Un flag separato dalle ore lascerebbe possibile lo stato
 * "part-time, otto ore", che non vuol dire niente e che nessuno saprebbe poi
 * come interpretare. Togliendo la spunta le ore tornano nulle, cioe' tempo
 * pieno.
 *
 * Il numero digitato vive in uno stato suo perche' mentre si scrive passa per
 * valori senza senso - vuoto, "0", "12" - e propagarli farebbe partire una
 * chiamata per ognuno. Si propaga all'uscita dal campo, e solo se sta dentro
 * l'intervallo; altrimenti si torna al valore buono di prima.
 */
function SceltaOrario({
  orario,
  disabilitato = false,
  onOrario,
}: {
  orario: number[];
  disabilitato?: boolean;
  onOrario: (orario: number[]) => void;
}) {
  // Le caselle vivono in uno stato loro perche' mentre si scrive passano per
  // valori senza senso - vuoto, "1,", "99" - e propagarli farebbe partire una
  // chiamata per ognuno. Si propaga all'uscita dal campo, e solo se il valore
  // sta nell'intervallo; altrimenti si torna a quello buono di prima.
  const [bozza, setBozza] = useState(orario.map(scrivi));
  const [ultimo, setUltimo] = useState(orario.join('|'));
  if (orario.join('|') !== ultimo) {
    setUltimo(orario.join('|'));
    setBozza(orario.map(scrivi));
  }

  const settimana = orario.reduce((somma, ore) => somma + ore, 0);
  const giorniLavorati = orario.filter((ore) => ore > 0).length;

  function applica(nuovoOrario: number[]): void {
    setBozza(nuovoOrario.map(scrivi));
    onOrario(nuovoOrario);
  }

  return (
    <>
      <div className={stili.modelli}>
        {MODELLI.map((m) => (
          <button
            key={m.etichetta}
            type="button"
            className={`${stili.modello} ${
              orario.join('|') === m.orario.join('|') ? stili.modelloAttivo : ''
            }`}
            disabled={disabilitato}
            onClick={() => applica([...m.orario])}
          >
            {m.etichetta}
          </button>
        ))}
      </div>

      <div className={stili.orario}>
        {GIORNI_SETTIMANA.map((giorno, i) => (
          <label key={giorno} className={stili.giornoOrario}>
            <span className={stili.giornoNome}>{giorno}</span>
            <input
              type="text"
              className={stili.inputOre}
              inputMode="decimal"
              value={bozza[i] ?? ''}
              disabled={disabilitato}
              aria-label={`Ore del ${giorno}`}
              onChange={(e) => {
                const copia = [...bozza];
                copia[i] = e.target.value;
                setBozza(copia);
              }}
              onBlur={() => {
                const n = Number((bozza[i] ?? '').replace(',', '.'));
                const valido = Number.isFinite(n) && n >= 0 && n <= 24;
                const nuoveOre = valido ? n : (orario[i] ?? 0);
                // Una settimana tutta a zero non e' un orario: e' una persona
                // che non potrebbe chiedere niente, e il server la rifiuta.
                const proposto = orario.map((ore, k) => (k === i ? nuoveOre : ore));
                if (proposto.every((ore) => ore === 0)) {
                  setBozza(orario.map(scrivi));
                  return;
                }
                if (proposto.join('|') !== orario.join('|')) applica(proposto);
                else setBozza(orario.map(scrivi));
              }}
            />
          </label>
        ))}
      </div>

      <span className={stili.aiuto}>
        {giorniLavorati} giorni lavorati, {scrivi(settimana)} ore a settimana. Zero vuol
        dire che quel giorno non si lavora: le ferie non lo contano e non ci si possono
        chiedere ore.
      </span>
    </>
  );
}

function Scheda({
  persona,
  reparti,
  eSeStesso,
  onAggiornata,
  onCredenziali,
}: {
  persona: VoceAnagrafica;
  reparti: RepartoDisponibile[];
  eSeStesso: boolean;
  onAggiornata: () => Promise<void>;
  onCredenziali: (c: CredenzialiProvvisorie) => void;
}) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [dataCessazione, setDataCessazione] = useState(oggi);
  const [confermaCessazione, setConfermaCessazione] = useState(false);

  async function esegui(azione: () => Promise<unknown>) {
    setErrore(null);
    setInCorso(true);
    try {
      await azione();
      await onAggiornata();
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Operazione non riuscita.');
    } finally {
      setInCorso(false);
    }
  }

  const sediUniche = useMemo(() => {
    const m = new Map<string, { id: string; nome: string }>();
    for (const r of reparti) m.set(r.sedeId, { id: r.sedeId, nome: r.sedeNome });
    return [...m.values()];
  }, [reparti]);

  return (
    <div className={stili.scheda}>
      <h2 className={stili.titolo}>{persona.nomeCompleto}</h2>
      <p className={stili.sottotitolo}>
        {persona.stato === 'CESSATO' ? (
          <span className={stili.pastigliaCessato}>
            Rapporto chiuso il {italiana(persona.dataCessazione)}
          </span>
        ) : (
          <>In forza{persona.dataAssunzione && ` dal ${italiana(persona.dataAssunzione)}`}</>
        )}
      </p>

      {errore && <p className={stili.errore}>{errore}</p>}

      <div className={stili.sezione}>
        <h3 className={stili.sezioneTitolo}>Accesso</h3>
        {persona.username ? (
          <dl className={stili.dati}>
            <dt>Nome utente</dt>
            <dd><code>{persona.username}</code></dd>
            <dt>Stato</dt>
            <dd>{persona.accessoAttivo ? 'attivo' : 'disattivato'}</dd>
            <dt>Ultimo accesso</dt>
            <dd>{persona.ultimoAccesso ?? 'mai entrato'}</dd>
            {persona.deveCambiarePassword && (
              <>
                <dt>Password</dt>
                <dd>ancora quella provvisoria</dd>
              </>
            )}
          </dl>
        ) : (
          <p className={stili.aiuto}>Questa persona non ha un accesso.</p>
        )}
        {persona.username && (
          <button
            type="button"
            className={stili.pulsanteSecondario}
            title={`Genera una nuova password provvisoria per ${persona.username}`}
            disabled={inCorso}
            onClick={() =>
              void esegui(async () => {
                const c = await api.azzeraPassword(persona.id);
                onCredenziali(c);
              })
            }
          >
            Azzera password
          </button>
        )}
      </div>

      <div className={stili.sezione}>
        <h3 className={stili.sezioneTitolo}>Collocazione e ruolo</h3>

        <label className={stili.campo}>
          <span className={stili.etichetta}>Sede e reparto</span>
          <select
            className={stili.input}
            value={persona.repartoId}
            disabled={inCorso}
            onChange={(e) =>
              void esegui(() => api.modificaPersona(persona.id, { repartoId: e.target.value }))
            }
          >
            {reparti.map((r) => (
              <option key={r.id} value={r.id}>
                {r.sedeNome} · {r.nome}
              </option>
            ))}
          </select>
        </label>

        <label className={stili.campo}>
          <span className={stili.etichetta}>Ruolo</span>
          <select
            className={stili.input}
            value={persona.ruolo}
            disabled={inCorso || eSeStesso}
            onChange={(e) =>
              void esegui(() =>
                api.modificaPersona(persona.id, { ruolo: e.target.value as Ruolo }),
              )
            }
          >
            {RUOLI.map((r) => (
              <option key={r.codice} value={r.codice}>
                {r.etichetta}
              </option>
            ))}
          </select>
          {eSeStesso && (
            <span className={stili.aiuto}>
              Non puoi cambiare il tuo stesso ruolo: se ti togliessi i poteri per sbaglio
              non potresti più rimediare da solo.
            </span>
          )}
        </label>

        <label className={stili.campo}>
          <span className={stili.etichetta}>Email (facoltativa)</span>
          <input
            type="email"
            className={stili.input}
            defaultValue={persona.email ?? ''}
            placeholder="lasciare vuoto se non ne ha una"
            disabled={inCorso}
            onBlur={(e) => {
              const valore = e.target.value.trim() || null;
              if (valore === (persona.email ?? null)) return;
              void esegui(() => api.modificaPersona(persona.id, { email: valore }));
            }}
          />
        </label>

        <label className={stili.campo}>
          <span className={stili.etichetta}>Codice fiscale (facoltativo)</span>
          <input
            className={stili.input}
            defaultValue={persona.codiceFiscale ?? ''}
            maxLength={16}
            placeholder="16 caratteri"
            disabled={inCorso}
            onBlur={(e) => {
              const valore = e.target.value.trim().toUpperCase() || null;
              if (valore === (persona.codiceFiscale ?? null)) return;
              void esegui(() =>
                api.modificaPersona(persona.id, { codiceFiscale: valore }),
              );
            }}
          />
        </label>

        <fieldset className={stili.campo}>
          <legend className={stili.etichetta}>Orario</legend>
          <SceltaOrario
            orario={persona.orarioSettimanale}
            disabilitato={inCorso}
            onOrario={(orario) => {
              if (orario.join('|') === persona.orarioSettimanale.join('|')) return;
              void esegui(() =>
                api.modificaPersona(persona.id, { orarioSettimanale: orario }),
              );
            }}
          />
        </fieldset>
      </div>

      <div className={stili.sezione}>
        <h3 className={stili.sezioneTitolo}>Deleghe di inserimento</h3>
        <p className={stili.aiuto}>
          Con una delega questa persona può inserire assenze per gli altri, dentro il
          perimetro assegnato. Le regole di preavviso restano le stesse per tutti.
        </p>

        {persona.deleghe.length === 0 ? (
          <p className={stili.aiuto}>Nessuna delega: inserisce solo per sé.</p>
        ) : (
          <ul className={stili.deleghe}>
            {persona.deleghe.map((d) => (
              <li key={d.id} className={stili.delega}>
                <span>
                  {d.sedeNome}
                  {d.repartoNome ? ` · ${d.repartoNome}` : ' · tutti i reparti'}
                </span>
                <button
                  type="button"
                  className={stili.revoca}
                  disabled={inCorso}
                  onClick={() => void esegui(() => api.revocaDelega(d.id))}
                >
                  Revoca
                </button>
              </li>
            ))}
          </ul>
        )}

        {persona.username && (
          <AggiungiDelega
            reparti={reparti}
            sedi={sediUniche}
            disabilitato={inCorso}
            onAggiungi={(sedeId, repartoId) =>
              esegui(() => api.concediDelega(persona.id, sedeId, repartoId))
            }
          />
        )}
      </div>

      <div className={stili.sezione}>
        <h3 className={stili.sezioneTitolo}>Rapporto di lavoro</h3>
        {persona.stato === 'CESSATO' ? (
          <>
            <p className={stili.aiuto}>
              Resta visibile nel calendario fino al {italiana(persona.dataCessazione)}, dove le
              sue assenze sono storia vera. Nei periodi successivi non compare.
            </p>
            <label className={stili.campo}>
              <span className={stili.etichetta}>Data di rientro</span>
              <input
                type="date"
                className={stili.input}
                value={dataCessazione}
                onChange={(e) => setDataCessazione(e.target.value)}
              />
            </label>
            <button
              type="button"
              className={stili.pulsanteSecondario}
              disabled={inCorso}
              onClick={() => void esegui(() => api.riattivaPersona(persona.id, dataCessazione))}
            >
              Riattiva
            </button>
          </>
        ) : eSeStesso ? (
          <p className={stili.aiuto}>Non puoi chiudere il tuo stesso rapporto di lavoro.</p>
        ) : !confermaCessazione ? (
          <button
            type="button"
            className={stili.pulsantePericolo}
            onClick={() => setConfermaCessazione(true)}
          >
            Chiudi il rapporto
          </button>
        ) : (
          <div className={stili.conferma}>
            <p className={stili.aiuto}>
              Da quel giorno {persona.nomeCompleto} non comparirà più nel calendario e non
              potrà più accedere. Le assenze già registrate restano.
            </p>
            <label className={stili.campo}>
              <span className={stili.etichetta}>Ultimo giorno di lavoro</span>
              <input
                type="date"
                className={stili.input}
                value={dataCessazione}
                onChange={(e) => setDataCessazione(e.target.value)}
              />
            </label>
            <div className={stili.azioni}>
              <button
                type="button"
                className={stili.pulsanteSecondario}
                onClick={() => setConfermaCessazione(false)}
              >
                Annulla
              </button>
              <button
                type="button"
                className={stili.pulsantePericolo}
                disabled={inCorso}
                onClick={() =>
                  void esegui(async () => {
                    await api.cessaPersona(persona.id, dataCessazione);
                    setConfermaCessazione(false);
                  })
                }
              >
                Conferma la chiusura
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AggiungiDelega({
  reparti,
  sedi,
  disabilitato,
  onAggiungi,
}: {
  reparti: RepartoDisponibile[];
  sedi: { id: string; nome: string }[];
  disabilitato: boolean;
  onAggiungi: (sedeId: string, repartoId: string | null) => Promise<void>;
}) {
  const [sedeId, setSedeId] = useState(sedi[0]?.id ?? '');
  const [repartoId, setRepartoId] = useState<string>('');

  const repartiDellaSede = reparti.filter((r) => r.sedeId === sedeId);

  return (
    <div className={stili.aggiungiDelega}>
      <select
        className={stili.input}
        value={sedeId}
        disabled={disabilitato}
        onChange={(e) => {
          setSedeId(e.target.value);
          setRepartoId('');
        }}
      >
        {sedi.map((s) => (
          <option key={s.id} value={s.id}>
            {s.nome}
          </option>
        ))}
      </select>

      <select
        className={stili.input}
        value={repartoId}
        disabled={disabilitato}
        onChange={(e) => setRepartoId(e.target.value)}
      >
        <option value="">tutti i reparti della sede</option>
        {repartiDellaSede.map((r) => (
          <option key={r.id} value={r.id}>
            {r.nome}
          </option>
        ))}
      </select>

      <button
        type="button"
        className={stili.pulsanteSecondario}
        disabled={disabilitato || sedeId === ''}
        onClick={() => void onAggiungi(sedeId, repartoId === '' ? null : repartoId)}
      >
        Aggiungi
      </button>
    </div>
  );
}

function italiana(data: string | null): string {
  if (!data) return '';
  return data.split('-').reverse().join('/');
}
